import { useState, useEffect, useCallback, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  ArrowLeftCircle,
  RefreshCw,
  AlertCircle,
  Shield,
  DollarSign,
  MessageSquare,
  Zap,
  Users,
} from 'lucide-react'
import { NavLink, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import ROUTES from '@/routes/routes'
import {
  PlayerResourceLimitsResponse,
  PlayerResourceLimitData,
} from '@/redux/types/resourceLimits'
import { ResourceEnforcementMode, GameRoomStatus } from '@/redux/types/room'
import { ResourceLimitsSkeleton } from '@/components/settings/ResourceLimits/ResourceLimitsSkeleton'
import RoomNotFound from '@/components/PageNotFound/RoomNotFound'
import { ResourceTable } from '@/components/settings/ResourceLimits/ResourceTable'
import { ReadOnlyBadge } from '@/components/settings/ResourceLimits/ReadOnlyBadge'
import { InfoTooltip } from '@/components/settings/ResourceLimits/LimitInput'
import { cn } from '@/lib/utils'
import {
  getPlayerResourceLimits,
  updatePlayerResourceLimits,
} from '@/api/resources'
import { useSelector } from 'react-redux'
import { RootState } from '@/redux/store'
import { useAppDispatch } from '@/redux/hooks'
import { getRoomDetails } from '@/redux/asyncThunks/room'

export default function ResourceLimitsView() {
  const dispatch = useAppDispatch()
  const params = useParams()
  const roomId = params.roomId as string

  const { currentRoom } = useSelector((state: RootState) => state.room)

  const isGameCompleted = useMemo(
    () => currentRoom?.status === GameRoomStatus.COMPLETED,
    [currentRoom?.status]
  )

  const [data, setData] = useState<PlayerResourceLimitsResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editValues, setEditValues] = useState<{
    message_limit: number | null
    token_limit: number | null
    currency_limit: number | null
  } | null>(null)
  const [isUpdating, setIsUpdating] = useState(false)

  const fetchData = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) {
        setIsRefreshing(true)
      } else {
        setIsLoading(true)
      }
      setError(null)

      try {
        const response = await getPlayerResourceLimits(roomId)
        setData(response)
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch resource limits'
        setError(errorMessage)
        toast.error(errorMessage)
      } finally {
        setIsLoading(false)
        setIsRefreshing(false)
      }
    },
    [roomId]
  )

  useEffect(() => {
    if (roomId) {
      dispatch(getRoomDetails(roomId))
      fetchData()
    }
  }, [roomId, fetchData, dispatch])

  const handleRefresh = useCallback(() => {
    fetchData(true)
  }, [fetchData])

  const handleEdit = (limit: PlayerResourceLimitData) => {
    if (isGameCompleted) return
    setEditingId(limit.id)
    setEditValues({
      message_limit: limit.message_limit,
      token_limit: limit.token_limit,
      currency_limit: limit.currency_limit,
    })
  }

  const handleCancelEdit = useCallback(() => {
    setEditingId(null)
    setEditValues(null)
  }, [])

  const handleSave = useCallback(
    async (limit: PlayerResourceLimitData) => {
      if (!editValues || isGameCompleted) return

      setIsUpdating(true)
      try {
        await updatePlayerResourceLimits(roomId, [
          {
            player_id: limit.player.room_member_id,
            message_limit: editValues.message_limit,
            token_limit: editValues.token_limit,
            currency_limit: editValues.currency_limit,
          },
        ])

        toast.success('Resource limits updated successfully')
        setEditingId(null)
        setEditValues(null)
        await fetchData(true)
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to update resource limits'
        toast.error(errorMessage)
      } finally {
        setIsUpdating(false)
      }
    },
    [editValues, roomId, fetchData, isGameCompleted]
  )

  const handleInputChange = useCallback(
    (
      field: 'message_limit' | 'token_limit' | 'currency_limit',
      value: string
    ) => {
      if (!editValues) return
      const numValue = value === '' ? null : parseInt(value, 10)
      if (numValue === null || (!isNaN(numValue) && numValue >= 0)) {
        setEditValues({
          ...editValues,
          [field]: numValue,
        })
      }
    },
    [editValues]
  )

  const playerLimitsArray = useMemo(
    () => data?.player_limits ?? [],
    [data?.player_limits]
  )

  const stats = useMemo(() => {
    if (!data) return []

    return [
      {
        label: 'Enforcement Mode',
        value:
          data.resource_enforcement_mode === ResourceEnforcementMode.BLOCK
            ? 'Block Action'
            : 'Warn Only',
        icon: Shield,
        color: 'text-sky-600',
        bgColor: 'bg-sky-50',
        borderColor: 'border-sky-200',
      },
      {
        label: 'Default Currency',
        value: data.default_currency_amount ?? 'Not set',
        icon: DollarSign,
        color: 'text-emerald-600',
        bgColor: 'bg-emerald-50',
        borderColor: 'border-emerald-200',
      },
      {
        label: 'Default Messages',
        value: data.default_message_limit ?? 'Not set',
        icon: MessageSquare,
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
      },
      {
        label: 'Default Tokens',
        value: data.default_token_limit ?? 'Not set',
        icon: Zap,
        color: 'text-purple-600',
        bgColor: 'bg-purple-50',
        borderColor: 'border-purple-200',
      },
      {
        label: 'Total Players',
        value: playerLimitsArray.length,
        icon: Users,
        color: 'text-indigo-600',
        bgColor: 'bg-indigo-50',
        borderColor: 'border-indigo-200',
      },
    ]
  }, [data, playerLimitsArray.length])

  const resourceLimitsEnabled = data?.resource_limits_enabled ?? false

  if (isLoading) {
    return <ResourceLimitsSkeleton />
  }

  if (error && !data) {
    return (
      <div className='flex items-center justify-center w-full h-full text-center'>
        <RoomNotFound message='Unable to fetch the details.' />
      </div>
    )
  }

  if (!data) {
    return null
  }

  return (
    <div className='relative px-6 md:px-12 lg:px-24 py-6 space-y-6 max-w-7xl mx-auto'>
      <div className='flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between'>
        <div className='flex gap-3 items-center'>
          <NavLink
            to={ROUTES.PROFESSOR.ROOM_CONFIGURE_RESOURCE_LIMITS(roomId)}
            className='flex-shrink-0'
          >
            <ArrowLeftCircle className='text-dark-blue hover:text-dark-blue-hovered transition-colors' />
          </NavLink>
          <div>
            <h1 className='text-2xl font-bold text-dark-blue'>
              Player Resource Limits
            </h1>
          </div>
        </div>
        <div className='flex items-center gap-2'>
          {isGameCompleted && <ReadOnlyBadge />}
          <Button
            onClick={handleRefresh}
            disabled={isRefreshing}
            variant='outline'
            size='sm'
            className='shadow-sm hover:shadow-md transition-all duration-200'
          >
            <RefreshCw
              className={cn('h-4 w-4 mr-2', isRefreshing && 'animate-spin')}
            />
            Refresh
          </Button>
        </div>
      </div>

      {!resourceLimitsEnabled && (
        <Card className='p-4 bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-200 shadow-sm'>
          <div className='flex items-start gap-3'>
            <AlertCircle className='h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5' />
            <div className='flex-1'>
              <p className='text-sm font-semibold text-amber-900 mb-1'>
                Resource Limits Not Enabled
              </p>
              <p className='text-sm text-amber-800'>
                Resource limits are not enabled for this room. Enable them in
                Resource Limits Settings to view and manage player limits.
              </p>
            </div>
          </div>
        </Card>
      )}

      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4'>
        {stats.map((stat, index) => {
          const Icon = stat.icon
          return (
            <Card
              key={index}
              className={cn(
                'p-4 border-2 transition-all duration-200 hover:shadow-md',
                stat.bgColor,
                stat.borderColor
              )}
            >
              <div className='flex items-start justify-between mb-2'>
                <div className={cn('p-2 rounded-lg', stat.bgColor)}>
                  <Icon className={cn('h-4 w-4', stat.color)} />
                </div>
              </div>
              <p className='text-xs font-medium text-slate-600 mb-1'>
                {stat.label}
              </p>
              <p className='text-base font-bold text-slate-800'>{stat.value}</p>
            </Card>
          )
        })}
      </div>

      <ResourceTable
        playerLimits={playerLimitsArray}
        resourceLimitsEnabled={resourceLimitsEnabled}
        editingId={editingId}
        editValues={editValues}
        isUpdating={isUpdating}
        isGameCompleted={isGameCompleted}
        onEdit={handleEdit}
        onSave={handleSave}
        onCancelEdit={handleCancelEdit}
        onInputChange={handleInputChange}
      />

      <Card className='p-5 bg-gradient-to-br from-sky-50 via-indigo-50 to-sky-50 border-2 border-sky-200 shadow-sm'>
        <div className='flex items-start gap-3'>
          <div className='p-2 bg-sky-100 rounded-lg'>
            <InfoTooltip
              content={
                <p>
                  Resource limits are automatically created when players are
                  invited. Limits can be customized per player by clicking the
                  edit icon.
                </p>
              }
            />
          </div>
          <div className='flex-1'>
            <p className='font-semibold text-slate-800 mb-2'>
              About Resource Limits
            </p>
            <p className='text-sm text-slate-700 leading-relaxed'>
              Players receive default limits when invited. Individual limits can
              be adjusted by editing each player's row. The enforcement mode
              determines whether actions are blocked or only warned when limits
              are exceeded. For token limits, we recommend{' '}
              <span className='font-semibold'>500–800 tokens</span> as minimum
              and <span className='font-semibold'>1,000–1,500 tokens</span> for
              balanced usage.
            </p>
          </div>
        </div>
      </Card>
    </div>
  )
}
